import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import {
  Bug,
  Lightbulb,
  Wrench,
  Copy,
  Check,
  Flame,
  ChevronUp,
  ChevronDown,
  ArrowRight,
  Trash2,
  ExternalLink,
  ClipboardList,
  Rocket,
  CheckCircle2,
  RefreshCw,
  Tag,
  Box,
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
  Search,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const PRODUCT_ICON_MAP = { Building2, Globe, Calculator, Users, Inbox, Box };

const PRODUCT_COLOR_CLASSES = {
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};
const productClass = (color) => PRODUCT_COLOR_CLASSES[color] || PRODUCT_COLOR_CLASSES.zinc;

const TYPE_CONFIG = {
  bug:         { label: "Bug",         icon: Bug,      color: "text-red-400 border-red-500/50 bg-red-500/10" },
  feature:     { label: "Feature",     icon: Lightbulb, color: "text-green-400 border-green-500/50 bg-green-500/10" },
  improvement: { label: "Improvement", icon: Wrench,   color: "text-blue-400 border-blue-500/50 bg-blue-500/10" },
};

const PRIORITY_CONFIG = {
  critical: { label: "Critical", color: "text-red-400 bg-red-500/10 border-red-500/50",     icon: Flame },
  high:     { label: "High",     color: "text-orange-400 bg-orange-500/10 border-orange-500/50", icon: ChevronUp },
  normal:   { label: "Normal",   color: "text-blue-400 bg-blue-500/10 border-blue-500/50",  icon: ArrowRight },
  low:      { label: "Low",      color: "text-zinc-400 bg-zinc-500/10 border-zinc-500/50",  icon: ChevronDown },
};

const STATUS_CONFIG = {
  backlog:     { label: "Backlog",      color: "text-zinc-400 bg-zinc-800 border-zinc-700" },
  todo:        { label: "To Do",        color: "text-orange-400 bg-orange-500/10 border-orange-500/50" },
  in_progress: { label: "In Progress",  color: "text-blue-400 bg-blue-500/10 border-blue-500/50" },
  done:        { label: "Done",         color: "text-green-400 bg-green-500/10 border-green-500/50" },
};

const SENTIMENT_CONFIG = {
  fire:      { emoji: "🔥", label: "Fire — building it",  color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  good:      { emoji: "👍", label: "Good idea",           color: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  neutral:   { emoji: "🤔", label: "Considering",         color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  weak:      { emoji: "👎", label: "Weak fit",            color: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
  wont_ship: { emoji: "🚫", label: "Won't ship",          color: "bg-red-500/15 text-red-300 border-red-500/40" },
};

const activeStatuses = ["backlog", "todo", "in_progress"];

const DevBacklogPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSource = (() => {
    const v = new URLSearchParams(location.search).get("source");
    return v === "partner" || v === "internal" ? v : "all";
  })();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterType, setFilterType] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const [filterInstance, setFilterInstance] = useState("all");
  const [filterSource, setFilterSource] = useState(initialSource);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [teamCommentDraft, setTeamCommentDraft] = useState("");
  const [postingTeamComment, setPostingTeamComment] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/dev-backlog");
      setItems(response.data || []);
    } catch {
      toast.error("Failed to load backlog");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get("/support-products", { params: { active_only: true } });
      setProducts(response.data || []);
    } catch (e) {
      console.error("[backlog] failed to load products", e);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const productBySlug = (slug) => products.find((p) => p.slug === slug) || null;

  const isPartnerItem = (item) => (
    item.source === "ai_triage_partner" || item.submitter_user_role === "partner"
  );

  const updateItem = async (itemId, updates) => {
    try {
      await api.put(`/dev-backlog/${itemId}`, updates);
      toast.success("Updated");
      setItems((prev) => prev.map((it) => it.id === itemId ? { ...it, ...updates } : it));
      if (selectedItem?.id === itemId) setSelectedItem((prev) => ({ ...prev, ...updates }));
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/dev-backlog/${itemId}`);
      toast.success("Deleted");
      if (selectedItem?.id === itemId) setSelectedItem(null);
      fetchItems();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copyPrompt = (item) => {
    navigator.clipboard.writeText(item.prompt_for_developer);
    setCopiedId(item.id);
    toast.success("Prompt copied!");
    setTimeout(() => setCopiedId(null), 2000);
    if (item.status === "backlog" || item.status === "todo") {
      updateItem(item.id, { status: "in_progress" });
    }
  };

  const postTeamComment = useCallback(async () => {
    if (!selectedItem || !teamCommentDraft.trim() || postingTeamComment) return;
    try {
      setPostingTeamComment(true);
      const { data: comment } = await api.post(`/dev-backlog/${selectedItem.id}/comment`, {
        content: teamCommentDraft.trim(),
      });
      const next = {
        ...selectedItem,
        comments: [...(selectedItem.comments || []), comment],
        updated_at: comment.created_at,
      };
      setSelectedItem(next);
      setItems((prev) => prev.map((it) => (it.id === selectedItem.id ? next : it)));
      setTeamCommentDraft("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post reply");
    } finally {
      setPostingTeamComment(false);
    }
  }, [selectedItem, teamCommentDraft, postingTeamComment]);

  const filtered = items.filter((item) => {
    if (filterStatus === "active" && !activeStatuses.includes(item.status)) return false;
    if (filterStatus === "done" && item.status !== "done") return false;
    if (filterType === "bugs" && item.type !== "bug") return false;
    if (filterType === "features" && item.type !== "feature" && item.type !== "improvement") return false;
    const slug = item.product_slug || "hub";
    if (filterProduct !== "all" && slug !== filterProduct) return false;
    if (filterInstance !== "all" && (item.product_instance_slug || "") !== filterInstance) return false;
    if (filterSource === "partner" && !isPartnerItem(item)) return false;
    if (filterSource === "internal" && isPartnerItem(item)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !item.title?.toLowerCase().includes(q) &&
        !item.description?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 };
  const sorted = [...filtered].sort((a, b) => {
    const pa = priorityOrder[a.priority] ?? 2;
    const pb = priorityOrder[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const stats = {
    total: items.filter((i) => activeStatuses.includes(i.status)).length,
    bugs: items.filter((i) => i.type === "bug" && activeStatuses.includes(i.status)).length,
    features: items.filter((i) => (i.type === "feature" || i.type === "improvement") && activeStatuses.includes(i.status)).length,
    done: items.filter((i) => i.status === "done").length,
  };

  return (
    <div className="space-y-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Dev Backlog</h1>
          <p className="text-sm text-zinc-500">Tasks extracted from support chats</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/changelog")} variant="outline" size="sm" className="border-zinc-700">
            <Tag className="w-4 h-4 mr-1" /> Changelog
          </Button>
          <Button onClick={fetchItems} variant="outline" size="sm" className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { icon: ClipboardList, color: "blue",  label: "Active",    value: stats.total },
          { icon: Bug,           color: "red",   label: "Bugs",      value: stats.bugs },
          { icon: Lightbulb,     color: "green", label: "Features",  value: stats.features },
          { icon: CheckCircle2,  color: "green", label: "Completed", value: stats.done },
        ].map((s, i) => (
          <Card key={i} className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg bg-${s.color}-500/20 flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 text-${s.color}-400`} />
              </div>
              <div>
                <p className="text-xl font-bold text-zinc-50">{s.value}</p>
                <p className="text-[10px] text-zinc-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters — one compact row like SupportChatsPage */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32 h-8 text-xs bg-zinc-900 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36 h-8 text-xs bg-zinc-900 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="bugs">Bugs</SelectItem>
            <SelectItem value="features">Features</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-36 h-8 text-xs bg-zinc-900 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
          </SelectContent>
        </Select>

        {products.length > 0 && (
          <Select value={filterProduct} onValueChange={(v) => { setFilterProduct(v); setFilterInstance("all"); }}>
            <SelectTrigger className="w-40 h-8 text-xs bg-zinc-900 border-zinc-800">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All products</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.slug} value={p.slug}>{p.short_label || p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(() => {
          const sel = products.find((p) => p.slug === filterProduct);
          const liveInstances = (sel?.instances || []).filter((i) => i.is_active !== false);
          if (filterProduct === "all" || liveInstances.length === 0) return null;
          return (
            <Select value={filterInstance} onValueChange={setFilterInstance}>
              <SelectTrigger className="w-44 h-8 text-xs bg-zinc-900 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                <SelectItem value="all">All deployments</SelectItem>
                {liveInstances.map((inst) => (
                  <SelectItem key={inst.slug} value={inst.slug}>{inst.label || inst.slug}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        })()}

        <div className="flex-1 relative min-w-[160px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-8 text-sm bg-zinc-900 border-zinc-800"
          />
        </div>
      </div>

      {/* Split panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 520 }}>

        {/* Left — compact item list */}
        <div className="lg:col-span-2 space-y-1.5 max-h-[680px] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-5 h-5 animate-spin text-zinc-600" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12">
              <Rocket className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
              <p className="text-sm text-zinc-600">No tasks match the current filter.</p>
            </div>
          ) : sorted.map((item) => {
            const typeConf = TYPE_CONFIG[item.type] || TYPE_CONFIG.bug;
            const priConf = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.normal;
            const stsConf = STATUS_CONFIG[item.status] || STATUS_CONFIG.backlog;
            const TypeIcon = typeConf.icon;
            const PriIcon = priConf.icon;
            const isSelected = selectedItem?.id === item.id;
            const ip = productBySlug(item.product_slug || "hub");
            const ProdIcon = ip ? (PRODUCT_ICON_MAP[ip.icon] || Box) : null;

            return (
              <div
                key={item.id}
                onClick={() => { setSelectedItem(item); setTeamCommentDraft(""); }}
                className={cn(
                  "p-3 rounded-lg border cursor-pointer transition-all",
                  isSelected
                    ? "bg-zinc-800/70 border-orange-500/50"
                    : "border-zinc-800/50 hover:bg-zinc-800/30 hover:border-zinc-700",
                  item.priority === "critical" && !isSelected && "border-l-2 border-l-red-500",
                  item.priority === "high" && !isSelected && "border-l-2 border-l-orange-500"
                )}
                data-testid={`backlog-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <TypeIcon className={cn("w-3.5 h-3.5 flex-shrink-0", typeConf.color.split(" ")[0])} />
                    <p className="text-sm text-zinc-200 truncate font-medium">{item.title}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <PriIcon className={cn("w-3 h-3", priConf.color.split(" ")[0])} />
                    <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0", stsConf.color)}>
                      {stsConf.label}
                    </Badge>
                  </div>
                </div>

                {item.description && (
                  <p className="text-xs text-zinc-500 mt-1 truncate">{item.description}</p>
                )}

                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-1 flex-wrap">
                    {ip && ProdIcon && (
                      <span className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]",
                        productClass(ip.color)
                      )}>
                        <ProdIcon className="w-2.5 h-2.5" />
                        {ip.short_label || ip.name}
                      </span>
                    )}
                    {isPartnerItem(item) && (
                      <span className="text-[9px] text-violet-400">🤖 partner</span>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-600">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right — detail panel */}
        <Card className="lg:col-span-3 bg-zinc-900/50 border-zinc-800 flex flex-col">
          {!selectedItem ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ClipboardList className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                <p className="text-sm text-zinc-600">Select a task</p>
              </div>
            </div>
          ) : (
            <>
              {/* Panel header */}
              <CardHeader className="pb-3 border-b border-zinc-800">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-base font-semibold text-zinc-100 leading-snug">{selectedItem.title}</h2>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {selectedItem.chat_session_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 border-zinc-700 text-xs"
                        onClick={() => navigate(`/settings/support-chats?session=${selectedItem.chat_session_id}`)}
                        title="Open source chat"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />Chat
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 border-red-500/50 text-red-400 hover:bg-red-500/10"
                      onClick={() => deleteItem(selectedItem.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Inline controls row */}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <Select value={selectedItem.type} onValueChange={(v) => updateItem(selectedItem.id, { type: v })}>
                    <SelectTrigger className={cn("h-7 w-auto px-2 text-xs rounded-full border gap-1 bg-transparent", TYPE_CONFIG[selectedItem.type]?.color)}>
                      {(() => { const Icon = TYPE_CONFIG[selectedItem.type]?.icon || Bug; return <Icon className="w-3 h-3" />; })()}
                      <span>{TYPE_CONFIG[selectedItem.type]?.label || selectedItem.type}</span>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {Object.entries(TYPE_CONFIG).map(([k, v]) => {
                        const Icon = v.icon;
                        return (
                          <SelectItem key={k} value={k}>
                            <span className="flex items-center gap-1.5"><Icon className="w-3 h-3" />{v.label}</span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>

                  <Select value={selectedItem.priority} onValueChange={(v) => updateItem(selectedItem.id, { priority: v })}>
                    <SelectTrigger className="w-28 h-7 text-xs bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={selectedItem.status} onValueChange={(v) => updateItem(selectedItem.id, { status: v })}>
                    <SelectTrigger className="w-32 h-7 text-xs bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {selectedItem.affected_area && (
                    <span className="text-xs text-zinc-500">{selectedItem.affected_area}</span>
                  )}
                </div>

                <p className="text-[11px] text-zinc-600 mt-1">
                  {selectedItem.reporter_name}
                  {isPartnerItem(selectedItem) && <span className="text-violet-400 ml-1.5">🤖 partner triage</span>}
                  {isPartnerItem(selectedItem) && !selectedItem.admin_reviewed_at && (
                    <span className="text-amber-400 ml-1.5">⚠ awaiting review</span>
                  )}
                  <span className="ml-1.5">{new Date(selectedItem.created_at).toLocaleDateString()}</span>
                </p>
              </CardHeader>

              {/* Scrollable body */}
              <CardContent className="flex-1 overflow-y-auto py-4 space-y-4" style={{ maxHeight: 520 }}>

                {/* Description */}
                {(() => {
                  // Prefer the new image_urls[] (extract-time persisted, all
                  // screenshots from the chat) and fall back to the single
                  // legacy image_url for older items.
                  const imgs = Array.isArray(selectedItem.image_urls) && selectedItem.image_urls.length > 0
                    ? selectedItem.image_urls
                    : (selectedItem.image_url ? [selectedItem.image_url] : []);
                  if (!selectedItem.description && imgs.length === 0) return null;
                  return (
                    <div>
                      <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Description</h4>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                        {selectedItem.description || "(screenshot attached)"}
                      </p>
                      {selectedItem.reporter_pages?.length > 0 && (
                        <div className="mt-2 text-xs text-zinc-500">
                          <span className="text-zinc-600">Reported from: </span>
                          {selectedItem.reporter_pages.map((p, i) => (
                            <span key={i}>
                              {i > 0 && <span className="text-zinc-700"> · </span>}
                              <code className="text-zinc-400 bg-zinc-800/60 px-1 py-0.5 rounded text-[11px]">{p}</code>
                            </span>
                          ))}
                        </div>
                      )}
                      {imgs.length > 0 && (
                        <div className={cn(
                          "mt-2 grid gap-2",
                          imgs.length === 1 ? "grid-cols-1" : "grid-cols-2"
                        )}>
                          {imgs.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                              <img
                                src={url}
                                alt={`Attached screenshot ${i + 1}`}
                                className="max-h-64 max-w-full rounded-lg border border-zinc-700 hover:border-orange-500 transition-colors"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {selectedItem.steps_to_reproduce?.length > 0 && (
                  <div>
                    <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Steps to Reproduce</h4>
                    <ol className="list-decimal list-inside text-sm text-zinc-400 space-y-0.5">
                      {selectedItem.steps_to_reproduce.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                  </div>
                )}

                {selectedItem.expected_behavior && (
                  <div>
                    <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Expected Behavior</h4>
                    <p className="text-sm text-zinc-400">{selectedItem.expected_behavior}</p>
                  </div>
                )}

                {/* Prompt for developer */}
                <div>
                  <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Prompt for Developer</h4>
                  <div className="relative">
                    <pre className="text-sm text-zinc-300 bg-zinc-800/80 rounded-lg p-3 whitespace-pre-wrap border border-zinc-700 max-h-48 overflow-y-auto pr-16">
                      {selectedItem.prompt_for_developer}
                    </pre>
                    <Button
                      size="sm"
                      className={cn(
                        "absolute top-2 right-2 h-7",
                        copiedId === selectedItem.id ? "bg-green-600" : "bg-purple-600 hover:bg-purple-700"
                      )}
                      onClick={() => copyPrompt(selectedItem)}
                    >
                      {copiedId === selectedItem.id ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                      {copiedId === selectedItem.id ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                </div>

                {/* Original chat excerpt — collapsed by default, click to expand.
                    Lets admin verify the AI's extracted description against the
                    user's exact wording. */}
                {selectedItem.chat_excerpt && (
                  <details className="text-xs text-zinc-400 group">
                    <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300 select-none list-none flex items-center gap-1">
                      <span className="inline-block transition-transform group-open:rotate-90">▶</span>
                      <span className="uppercase tracking-wider text-[10px]">Original chat excerpt</span>
                    </summary>
                    <pre className="mt-2 bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 whitespace-pre-wrap max-h-72 overflow-y-auto text-zinc-400">
                      {selectedItem.chat_excerpt}
                    </pre>
                  </details>
                )}

                {/* Partner review block — only for partner items */}
                {isPartnerItem(selectedItem) && (
                  <div className="border border-violet-500/30 bg-violet-500/5 rounded-lg p-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs uppercase tracking-wider text-violet-300">
                        Partner review (shown to {selectedItem.reporter_name || "partner"})
                      </h4>
                      {selectedItem.admin_reviewed_at && (
                        <span className="text-[10px] text-zinc-500">
                          Reviewed {new Date(selectedItem.admin_reviewed_at).toLocaleDateString()}
                          {selectedItem.admin_reviewed_by ? ` · ${selectedItem.admin_reviewed_by}` : ""}
                        </span>
                      )}
                    </div>
                    {selectedItem.ai_reasoning && (
                      <div className="text-[11px] text-zinc-500 italic">
                        🤖 AI triage: {selectedItem.ai_reasoning}
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-zinc-500 uppercase">Override category</label>
                        <Select
                          value={selectedItem.admin_category_override || "__inherit__"}
                          onValueChange={(v) => updateItem(selectedItem.id, { admin_category_override: v === "__inherit__" ? null : v })}
                        >
                          <SelectTrigger className="h-8 text-xs bg-zinc-800 border-zinc-700 mt-0.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="__inherit__">Use AI ({selectedItem.type || "feature"})</SelectItem>
                            <SelectItem value="bug">Bug</SelectItem>
                            <SelectItem value="feature">Feature</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] text-zinc-500 uppercase">Priority for partner</label>
                        <Select
                          value={selectedItem.admin_priority || "__none__"}
                          onValueChange={(v) => updateItem(selectedItem.id, { admin_priority: v === "__none__" ? null : v })}
                        >
                          <SelectTrigger className="h-8 text-xs bg-zinc-800 border-zinc-700 mt-0.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="__none__">— not set —</SelectItem>
                            <SelectItem value="p0">P0 — urgent</SelectItem>
                            <SelectItem value="p1">P1 — high</SelectItem>
                            <SelectItem value="p2">P2 — medium</SelectItem>
                            <SelectItem value="p3">P3 — low</SelectItem>
                            <SelectItem value="rejected">Won't ship</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] text-zinc-500 uppercase">ETA</label>
                        <input
                          type="text"
                          value={selectedItem.admin_estimated_time || ""}
                          onChange={(e) => updateItem(selectedItem.id, { admin_estimated_time: e.target.value })}
                          placeholder="e.g. 2 weeks, Q2"
                          className="w-full h-8 text-xs bg-zinc-800 border border-zinc-700 rounded px-2 text-zinc-100 mt-0.5 placeholder:text-zinc-600"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1.5 block">
                        Team sentiment <span className="text-zinc-600 normal-case">(partner sees this)</span>
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(SENTIMENT_CONFIG).map(([key, conf]) => {
                          const current = (selectedItem.team_sentiment || {}).rating || "";
                          const active = current === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => updateItem(selectedItem.id, {
                                team_sentiment: active
                                  ? ""
                                  : { rating: key, note: (selectedItem.team_sentiment || {}).note || "" },
                              })}
                              className={cn(
                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs transition-all",
                                active
                                  ? `${conf.color} ring-1 ring-offset-1 ring-offset-zinc-900 ring-current`
                                  : "border-zinc-700 bg-zinc-800/40 text-zinc-400 hover:bg-zinc-700/60"
                              )}
                            >
                              <span>{conf.emoji}</span>
                              <span>{conf.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase">Reasoning shown to partner</label>
                      <textarea
                        value={selectedItem.admin_reasoning || ""}
                        onChange={(e) => updateItem(selectedItem.id, { admin_reasoning: e.target.value })}
                        rows={3}
                        placeholder="Especially for 'won't ship': explain WHY. Partners see this on /my-feedback."
                        className="w-full text-xs bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 mt-0.5 placeholder:text-zinc-600 resize-y"
                      />
                    </div>

                    <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedItem.partner_visible !== false}
                        onChange={(e) => updateItem(selectedItem.id, { partner_visible: e.target.checked })}
                        className="accent-violet-500"
                      />
                      Visible to partner on /my-feedback
                    </label>
                  </div>
                )}

                {/* Thread: "Conversation with partner" or "Team notes" */}
                <div className="border-t border-zinc-800/60 pt-3">
                  <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">
                    {isPartnerItem(selectedItem) ? "Conversation with partner" : "Team notes"}
                  </h4>
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1 mb-2">
                    {(selectedItem.comments || []).length === 0 ? (
                      <p className="text-[11px] text-zinc-600 italic">
                        {isPartnerItem(selectedItem)
                          ? "No replies yet. Use the box below to respond — the partner sees your reply on /my-feedback."
                          : "No notes yet. Add internal comments visible only to the team."}
                      </p>
                    ) : (
                      (selectedItem.comments || []).map((c) => (
                        <div
                          key={c.id}
                          className={cn(
                            "flex gap-2",
                            isPartnerItem(selectedItem) && c.role === "partner" ? "justify-start" : "justify-end"
                          )}
                        >
                          <div className={cn(
                            "max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap",
                            isPartnerItem(selectedItem) && c.role === "partner"
                              ? "bg-blue-500/15 border border-blue-500/30 text-blue-100"
                              : "bg-zinc-800 border border-zinc-700 text-zinc-200"
                          )}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={cn(
                                "text-[10px] font-medium uppercase tracking-wide",
                                isPartnerItem(selectedItem) && c.role === "partner" ? "text-blue-300" : "text-zinc-400"
                              )}>
                                {c.role === "partner" ? (c.author_name || "Partner") : (c.author_name || "Team")}
                              </span>
                              <span className="text-[10px] text-zinc-500">
                                {new Date(c.created_at).toLocaleString()}
                              </span>
                            </div>
                            {c.content}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <textarea
                      value={teamCommentDraft}
                      onChange={(e) => setTeamCommentDraft(e.target.value)}
                      placeholder={isPartnerItem(selectedItem)
                        ? "Reply to the partner — this shows up on their /my-feedback page."
                        : "Add an internal note — visible to team only."}
                      className="flex-1 text-xs bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 placeholder:text-zinc-600 resize-y min-h-[56px]"
                      onKeyDown={async (e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && teamCommentDraft.trim() && !postingTeamComment) {
                          e.preventDefault();
                          await postTeamComment();
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 self-end"
                      disabled={!teamCommentDraft.trim() || postingTeamComment}
                      onClick={postTeamComment}
                    >
                      {postingTeamComment ? "Sending…" : "Reply"}
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">⌘/Ctrl + Enter to send</p>
                </div>

              </CardContent>
            </>
          )}
        </Card>

      </div>
    </div>
  );
};

export default DevBacklogPage;
